import { ReactElement, ReactNode } from 'react'
import styled from 'styled-components'
import Mono from '../../../ui/Mono/Mono'

export interface TableSummaryItem {
  key: string
  label: string
  value: ReactNode
  icon?: ReactNode
}

export interface TableSummaryProps {
  items: TableSummaryItem[]
}

/**
 * Summary strip for `GridTable`'s `summary` slot: a flat card of label/value cells sitting
 * between the toolbar and the column header. Content is fully module-defined via `items`.
 */
export const TableSummary = ({ items }: TableSummaryProps): ReactElement => (
  <Root>
    <Card>
      {items.map((item) => (
        <Cell key={item.key}>
          <Label>
            {item.icon && <IconSlot aria-hidden>{item.icon}</IconSlot>}
            {item.label}
          </Label>
          <Value>{item.value}</Value>
        </Cell>
      ))}
    </Card>
  </Root>
)

export default TableSummary

const Root = styled.div`
  flex-shrink: 0;
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
`

/*
 * 1px gap over a line-coloured background draws the cell dividers.
 * Cells grow to fill each wrapped row, so a short last row stretches instead of leaving holes.
 */
const Card = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1px;
  background: ${({ theme }) => theme.colors.line};
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: ${({ theme }) => theme.radii.md};
  overflow: hidden;
`

const Cell = styled.div`
  flex: 1 1 8.5rem;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 10px 14px;
  background: ${({ theme }) => theme.colors.bgLayout};
`

/* --summary-content-opacity is set by GridTable's collapsible summary (fades text while flattening). */
const Label = styled.span`
  opacity: var(--summary-content-opacity, 1);
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.text.sm};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.ink3};
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.sm};
  }
`

const IconSlot = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.icon};
`

const Value = styled(Mono)`
  opacity: var(--summary-content-opacity, 1);
  font-size: ${({ theme }) => theme.text.lg};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ink};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.lg};
  }
`
